import { brl } from "@/lib/api";
import { Button, Input, Select } from "@/components/ui-kit";
import { Trash2, Plus } from "lucide-react";
import { METHODS, METHOD_LABEL } from "./receipt";

/**
 * Formas de pagamento da venda.
 *
 * Começa como quatro botões grandes porque a venda de balcão quase sempre tem UM
 * pagamento só — e nesse caso o vendedor toca uma vez e acabou. A lista editável
 * (com valores e parcelas) só aparece depois do primeiro toque, quando a venda
 * de fato virou "dividida em várias formas".
 */
export default function PaymentSection({
  payments, remaining, onAdd, onChange, onRemove,
  cashPortion, received, onReceivedChange, change,
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-eyebrow uppercase tracking-wider text-slate-500">Pagamento</span>
        {payments.length > 0 && (
          <span className={`text-xs font-mono tabular-nums ${Math.abs(remaining) < 0.01 ? "text-emerald-600" : "text-amber-600"}`}
            data-testid="pos-remaining">Restante {brl(remaining)}</span>
        )}
      </div>

      {payments.length === 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pos-quick-pay">
          {METHODS.map(([m, l]) => (
            <button key={m} type="button" onClick={() => onAdd(m)} data-testid={`pos-pay-${m}`}
              className="text-xs font-semibold border border-slate-200 rounded-lg h-11 hover:border-brand-500 hover:text-brand-600 transition-colors">
              {l}
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {payments.map((p, i) => (
            <div key={i} className="flex items-center gap-2" data-testid="pos-payment-line">
              <Select value={p.method} onChange={(e) => onChange(i, "method", e.target.value)}
                aria-label={`Forma de pagamento ${i + 1}`} size="sm" className="w-auto px-2">
                {METHODS.map(([m, l]) => <option key={m} value={m}>{l}</option>)}
              </Select>
              <Input inputMode="decimal" value={p.amount} aria-label={`Valor em ${METHOD_LABEL[p.method] || p.method}`}
                onChange={(e) => onChange(i, "amount", e.target.value)} data-testid="pos-payment-amount"
                size="sm" mono className="flex-1 min-w-0 px-2" />
              {p.method === "cartao_credito" && (
                <Select value={p.installments} onChange={(e) => onChange(i, "installments", e.target.value)}
                  data-testid="pos-payment-installments" aria-label="Parcelas" size="sm" className="w-auto px-1">
                  {Array.from({ length: 12 }, (_, k) => k + 1).map((n) => <option key={n} value={n}>{n}x</option>)}
                </Select>
              )}
              <Button variant="ghost" size="iconSm" onClick={() => onRemove(i)}
                aria-label={`Remover pagamento ${i + 1}`} className="hover:text-red-600 shrink-0"><Trash2 size={15} /></Button>
            </div>
          ))}
          <Button variant="link" size="sm" icon={Plus} onClick={() => onAdd("dinheiro")} data-testid="pos-add-payment">
            Adicionar forma de pagamento
          </Button>
        </div>
      )}

      {/* Troco só existe quando há dinheiro na jogada. */}
      {cashPortion > 0 && (
        <div className="grid grid-cols-2 gap-2 items-center">
          <Input inputMode="decimal" value={received} aria-label="Valor recebido em dinheiro"
            onChange={(e) => onReceivedChange(e.target.value)} placeholder="Recebido"
            data-testid="pos-received-input" mono />
          <p className={`text-sm text-right font-mono tabular-nums font-semibold ${change != null && change < 0 ? "text-red-500" : "text-emerald-600"}`}
            data-testid="pos-change">
            {change == null ? "Troco: —" : change < 0 ? `Falta ${brl(-change)}` : `Troco: ${brl(change)}`}
          </p>
        </div>
      )}
    </div>
  );
}
